import PropTypes from "prop-types";
import React from "react";

import { Sup, Text, textPropTypes } from "@swa-ui/core";
import globalRules from "@swa-ui/core/assets/styles/globalRules.module.scss";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./Currency.module.scss";

/**
 * Currency provides a mechanism to display price amounts, in varying currencies, styles, sizes, in
 * a consistent way across all currency instances in a site.
 */
export const Currency = (props) => {
  const {
    amount,
    className,
    currencyCode = "USD",
    displayDecimal = false,
    maxValue = 999999999,
    roundingType = "none",
    showSignPrefix = false,
    showSymbol = true,
    superScriptSymbol = true,
    textProps: { className: textClassName, ...otherTextProps } = {},
  } = props;
  const value = parseFloat(`${amount}`);

  return (
    <Text {...otherTextProps} className={classNames(className, textClassName, styles.currency)}>
      {renderAccessibleInformation()}
      {renderSignPrefix()}
      {renderPrefixSymbol()}
      {renderValue()}
      {renderSuffixSymbol()}
    </Text>
  );

  function renderAccessibleInformation() {
    return <div className={globalRules.hiddenFromScreen}>{renderScreenReaderAmount()}</div>;
  }

  function renderScreenReaderAmount() {
    return i18n(getCurrencyLabelKey(), { value: getParsedValue() });
  }

  function renderSignPrefix() {
    const sign = amount > 0 && showSignPrefix ? "+" : amount < 0 ? "-" : "";

    return sign ? <span className={styles.signPrefix}>{sign}</span> : null;
  }

  function renderPrefixSymbol() {
    const symbol = showSymbol && (isCAD() || isMXN() || isUSD()) && "$";

    return symbol ? renderSymbol(symbol) : null;
  }

  function renderSuffixSymbol() {
    let symbol;

    if (showSymbol) {
      if (isPoints()) {
        symbol = "PTS";
      } else if (isPercentage()) {
        symbol = "%";
      }
    }

    return symbol ? renderSymbol(symbol) : null;
  }

  function renderSymbol(char) {
    return superScriptSymbol ? <Sup>{char}</Sup> : char;
  }

  function renderValue() {
    return (
      <Text>
        {getParsedValue()}
        {renderMaxValueSign()}
      </Text>
    );
  }

  function renderMaxValueSign() {
    return isMaxExceeded() ? "+" : "";
  }

  function getCurrencyLabelKey() {
    return `Currency__${currencyCode}_SCREEN_READER${isMaxExceeded() ? "_MAX" : ""}`;
  }

  function getParsedValue() {
    const roundedAmount = round();

    return Intl.NumberFormat("en-US", formatMoney(roundedAmount)).format(Math.abs(roundedAmount));
  }

  function formatMoney(roundedValue) {
    const currencyDecimalOptions = {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    };

    return !Number.isInteger(roundedValue) || displayDecimal ? currencyDecimalOptions : undefined;
  }

  function round() {
    const roundingMethods = {
      closest: Math.round,
      down: Math.floor,
      up: Math.ceil,
    };
    const workingAmount = getNormalizedAmount();

    return roundingMethods[roundingType]?.(workingAmount) || workingAmount;
  }

  function getNormalizedAmount() {
    return Math.min(maxValue, value);
  }

  function isCAD() {
    return currencyCode === "CAD";
  }

  function isMXN() {
    return currencyCode === "MXN";
  }

  function isUSD() {
    return currencyCode === "USD";
  }

  function isPoints() {
    return currencyCode === "POINTS";
  }

  function isPercentage() {
    return currencyCode === "PERCENTAGE";
  }

  function isMaxExceeded() {
    return value > maxValue;
  }
};

Currency.propTypes = {
  /** Content that will be rendered. */
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Currency types that are, or will, be supported. CAD and MXN are defined for future compatibility.
   */
  currencyCode: PropTypes.oneOf(["CAD", "MXN", "PERCENTAGE", "POINTS", "USD"]),

  /** Whether to display decimal point value. */
  displayDecimal: PropTypes.bool,

  /** If maxValue is given, the amount displayed will be capped to the value given for this prop. */
  maxValue: PropTypes.number,

  /** If not cents are desired, roundingType defines how to show whole value without decimal points. */
  roundingType: PropTypes.oneOf(["closest", "down", "none", "up"]),

  /** Displays a plus sign before the number. */
  showSignPrefix: PropTypes.bool,

  /**
   * Indicates if the currency symbol will be displayed next to the price amount. If displayed, the
   * size will determine if the symbol will be superscripted. For larger fonts, the superscripted
   * symbol will be a smaller font and top aligned.
   */
  showSymbol: PropTypes.bool,

  /** Indicates if the currency symbol should be superscripted. */
  superScriptSymbol: PropTypes.bool,

  /** Passes typography props to Text component. */
  textProps: PropTypes.shape(textPropTypes),
};
